import { useState, useLayoutEffect, useRef, useCallback } from 'react';
import Vimeo from '@vimeo/player';

const useVimeo = (videoLink) => {
    const vimeoVideoRef = useRef(null);
    const vimeoPlayerRef = useRef(null);
    const [video, setVideo] = useState(videoLink);
    const [failed, setFailed] = useState(false);
    const [player, setPlayer] = useState(null);

    useLayoutEffect(() => {
        setVideo(videoLink);
    }, [videoLink]);

    useLayoutEffect(() => {
        if (!videoLink || !vimeoVideoRef.current) {
            return undefined;
        }

        const el = vimeoVideoRef.current;
        let cancelled = false;

        setFailed(false);
        setPlayer(null);

        const init = async () => {
            try {
                if (vimeoPlayerRef.current) {
                    await vimeoPlayerRef.current.destroy();
                    vimeoPlayerRef.current = null;
                }

                const instance = new Vimeo(el, {
                    url: videoLink,
                    autoplay: false,
                    loop: false,
                    responsive: true,
                });

                if (cancelled) {
                    await instance.destroy();
                    return;
                }

                vimeoPlayerRef.current = instance;
                await instance.ready();

                if (cancelled) {
                    await instance.destroy();
                    return;
                }

                setPlayer(instance);
            } catch (err) {
                const current = vimeoPlayerRef.current;
                vimeoPlayerRef.current = null;
                if (current) {
                    current.destroy().catch(() => {});
                }
                if (!cancelled) {
                    setFailed(true);
                }
            }
        };

        init();

        return () => {
            cancelled = true;
            const current = vimeoPlayerRef.current;
            vimeoPlayerRef.current = null;
            setPlayer(null);
            if (current) {
                current.destroy().catch(() => {});
            }
        };
    }, [videoLink]);

    const handleStopVideo = useCallback(async () => {
        try {
            await vimeoPlayerRef.current?.pause();
        } catch (error) {
            console.log('Stop Video', error);
        }
    }, []);

    const handlePlayVideo = useCallback(async () => {
        try {
            await vimeoPlayerRef.current?.play();
        } catch (error) {
            console.log('Play Video', error);
        }
    }, []);

    return {
        player,
        vimeoVideoRef,
        video,
        loading: false,
        handleStopVideo,
        handlePlayVideo,
        failed,
    };
};

export default useVimeo;
